import type { ActionFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { episodeAPI, APIError } from "../../lib/api";
import { createEpisodeSchema, updateEpisodeSchema } from "../../schemas";
import { extractFieldErrors, parseFormData } from "../../utils";
import type { ActionResult } from "./auth.actions";

export async function createEpisodeAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { id: orgId, podcastId } = params;

  if (!orgId || !podcastId) {
    return { success: false, error: "Organization ID and Podcast ID are required" };
  }

  const formData = await parseFormData(request);

  // If an S3 key was provided (from upload), use it as the audio URL reference
  const resolvedAudioUrl = formData.audioS3Key
    ? `s3://${formData.audioS3Key}`
    : formData.audioUrl || undefined;

  // Handle checkbox fields and empty values
  const dataWithDefaults = {
    ...formData,
    explicit: formData.explicit === "true" || formData.explicit === "on",
    isPremium: formData.isPremium === "true" || formData.isPremium === "on",
    seasonNumber: formData.seasonNumber || undefined,
    episodeNumber: formData.episodeNumber || undefined,
    audioDurationSeconds: formData.audioDurationSeconds || undefined,
    audioFileSizeBytes: formData.audioFileSizeBytes || undefined,
    audioUrl: resolvedAudioUrl,
    transcriptUrl: formData.transcriptUrl || undefined,
    scheduledAt: formData.scheduledAt || undefined,
  };

  const result = createEpisodeSchema.safeParse(dataWithDefaults);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const episode = await episodeAPI.create(orgId, podcastId, {
      title: result.data.title,
      slug: result.data.slug || undefined,
      description: result.data.description || undefined,
      showNotes: result.data.showNotes || undefined,
      audioUrl: result.data.audioUrl || undefined,
      audioDurationSeconds: result.data.audioDurationSeconds || undefined,
      audioFileSizeBytes: result.data.audioFileSizeBytes || undefined,
      transcriptUrl: result.data.transcriptUrl || undefined,
      status: result.data.status,
      isPremium: result.data.isPremium,
      seasonNumber:
        typeof result.data.seasonNumber === "number" ? result.data.seasonNumber : undefined,
      episodeNumber:
        typeof result.data.episodeNumber === "number" ? result.data.episodeNumber : undefined,
      episodeType: result.data.episodeType,
      explicit: result.data.explicit,
      chapters: result.data.chapters,
      scheduledAt: result.data.scheduledAt || undefined,
    });
    return redirect(`/organizations/${orgId}/podcasts/${podcastId}/episodes/${episode.id}/edit`);
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function updateEpisodeAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, podcastId, episodeId } = params;

  if (!orgId || !podcastId || !episodeId) {
    return { success: false, error: "Organization ID, Podcast ID and Episode ID are required" };
  }

  const formData = await parseFormData(request);

  // If an S3 key was provided (from upload), use it as the audio URL reference
  const resolvedAudioUrl = formData.audioS3Key
    ? `s3://${formData.audioS3Key}`
    : formData.audioUrl || null;

  // Handle checkbox fields
  const dataWithDefaults = {
    ...formData,
    explicit: formData.explicit === "true" || formData.explicit === "on",
    isPremium: formData.isPremium === "true" || formData.isPremium === "on",
    seasonNumber: formData.seasonNumber || null,
    episodeNumber: formData.episodeNumber || null,
    audioDurationSeconds: formData.audioDurationSeconds || null,
    audioFileSizeBytes: formData.audioFileSizeBytes || null,
    audioUrl: resolvedAudioUrl,
    transcriptUrl: formData.transcriptUrl || null,
    scheduledAt: formData.scheduledAt || null,
  };

  const result = updateEpisodeSchema.safeParse(dataWithDefaults);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const episode = await episodeAPI.update(orgId, podcastId, episodeId, result.data);
    return { success: true, data: episode };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function deleteEpisodeAction({
  params,
}: ActionFunctionArgs): Promise<ActionResult | Response> {
  const { id: orgId, podcastId, episodeId } = params;

  if (!orgId || !podcastId || !episodeId) {
    return { success: false, error: "Organization ID, Podcast ID and Episode ID are required" };
  }

  try {
    await episodeAPI.delete(orgId, podcastId, episodeId);
    return redirect(`/organizations/${orgId}/podcasts/${podcastId}`);
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function publishEpisodeAction({ params }: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, podcastId, episodeId } = params;

  if (!orgId || !podcastId || !episodeId) {
    return { success: false, error: "Organization ID, Podcast ID and Episode ID are required" };
  }

  try {
    const episode = await episodeAPI.publish(orgId, podcastId, episodeId);
    return { success: true, data: episode };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}
